import { Stack } from '@/components/layout';
import { useSelectContext } from '@/components/ui/select/select-context';
import { useStyles } from '@/components/ui/select/styles';
import type { SelectTriggerProps } from '@/components/ui/select/types';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { FC, memo, useMemo, useRef } from 'react';
import { LayoutChangeEvent, Pressable, View } from 'react-native';

export const SelectTrigger: FC<SelectTriggerProps> = memo(
  ({
    style,
    labelVariant = 'Font Inter/Subtitle/Regular 14px',
    placeholder,
    hideIcon = false,
    disabled: disabledProp,
    onLayout,
    textColor,
    ...rest
  }) => {
    const {
      open,
      setOpen,
      disabled,
      options,
      selectedValue,
      placeholder: rootPlaceholder,
      status,
      setTriggerLayout,
    } = useSelectContext();
    const { scale } = useScaling();
    const styles = useStyles();
    const triggerRef = useRef<View>(null);

    const isDisabled = disabledProp ?? disabled;

    const selectedLabel = useMemo(() => {
      const opt = options.find(o => o.value === selectedValue);
      return opt?.label || null;
    }, [options, selectedValue]);

    const content = selectedLabel ?? placeholder ?? rootPlaceholder;
    const isPlaceholder = selectedLabel == null;
    const measureTriggerLayout = (onMeasured?: () => void) => {
      triggerRef.current?.measureInWindow((x, y, width, height) => {
        if (width > 0 && height > 0) {
          setTriggerLayout({ x, y, width, height });
          onMeasured?.();
          return;
        }
        // Fallback for cases where window measurement is not ready yet.
        triggerRef.current?.measure((_, __, measuredWidth, measuredHeight, pageX, pageY) => {
          if (measuredWidth <= 0 || measuredHeight <= 0) return;
          setTriggerLayout({ x: pageX, y: pageY, width: measuredWidth, height: measuredHeight });
          onMeasured?.();
        });
      });
    };

    const handlePress = () => {
      if (isDisabled) return;
      if (open) {
        setOpen(false);
        return;
      }

      requestAnimationFrame(() => {
        measureTriggerLayout(() => {
          setOpen(true);
        });
      });
    };

    const handleLayout = (event: LayoutChangeEvent) => {
      onLayout?.(event);
      requestAnimationFrame(() => {
        measureTriggerLayout();
      });
    };

    return (
      <Pressable
        ref={triggerRef}
        accessibilityRole="button"
        accessibilityState={{ expanded: open, disabled: isDisabled }}
        onPress={handlePress}
        onLayout={handleLayout}
        disabled={isDisabled}
        style={[
          styles.trigger,
          status === 'error' ? styles.triggerError : undefined,
          isDisabled ? styles.disabled : undefined,
          style,
        ]}
        {...rest}>
        <Text
          variant={labelVariant}
          style={[
            styles.triggerLabel,
            isPlaceholder ? styles.triggerPlaceholderLabel : undefined,
            textColor ? { color: textColor } : undefined,
          ]}
          numberOfLines={1}>
          {content}
        </Text>

        {!hideIcon ? (
          <Stack
            style={[
              styles.iconWrapper,
              {
                transform: [{ rotate: open ? '-90deg' : '90deg' }],
              },
            ]}>
            <SvgIcon name="IcPaginationArrow" size={scale(16)} color={textColor ?? colors.Blue} />
          </Stack>
        ) : null}
      </Pressable>
    );
  },
);

SelectTrigger.displayName = 'Select.Trigger';
